import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import {
  MdArrowDownward,
  MdArrowForward,
  MdOutlineArrowUpward,
} from "react-icons/md";
import { motion, useReducedMotion } from "framer-motion";
import {
  COUNT_UP_ANIMATION_DURATION,
  ETransactionType,
} from "src/helpers/constants";
import { memo, useCallback, useMemo, useState } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import Icon from "../Icon/Icon";
import { Link, useNavigate } from "react-router-dom";
import useCurrentUser from "src/hooks/useCurrentUser";
import {
  formatLongDate,
  rgbaObjectToString,
  transactionDescription,
} from "src/helpers/utils";
import ClientProviderInfoModal from "../ClientProviderInfoModal/ClientProviderInfoModal";
import useMobile from "src/hooks/useMobile";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { ROUTES } from "src/helpers/routes";
import useCurrencies from "src/hooks/useCurrencies";
import SmartImage from "../SmartImage/SmartImage";
import { ICON_SIZES } from "src/constants/icons";

const MOBILE_SIZE = 58;
const DESKTOP_SIZE = 64;

interface IPendingTransactionCard {
  transaction: any;
  index: number;
}

const PendingTransactionCard: React.FC<IPendingTransactionCard> = ({
  transaction,
  index,
}) => {
  const prefersReducedMotion = useReducedMotion();

  const { isMobile } = useMobile();
  const { getCurrencyDataByCode } = useCurrencies();
  const { t } = useTranslation();
  const { getSourceCustomSettings } = useAllTransactions();
  const [showSourceModal, setShowSourceModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const { getAccountById } = useCurrentUser();

  const sourceInfo = useMemo(() => {
    return getSourceCustomSettings(transaction?.payTo);
  }, [transaction]);

  const accountInfo = useMemo(() => {
    return getAccountById(transaction?.account);
  }, [transaction]);

  const currencySymbol = useMemo(() => {
    return (
      getCurrencyDataByCode({ code: accountInfo?.currency })?.symbol_native ||
      ""
    );
  }, [accountInfo, getCurrencyDataByCode]);

  const description = useMemo(() => {
    return transactionDescription(transaction);
  }, [transaction]);

  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  return (
    <>
      <motion.div
        className="flex w-full"
        {...(!prefersReducedMotion && {
          initial: {
            opacity: 0,
            x: index % 2 === 0 ? -100 : 100,
          },
          animate: { opacity: 1, x: 0 },
          transition: {
            duration: 0.2,
            ease: "easeOut",
            delay: index * 0.1,
          },
        })}
      >
        <Link
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "bg-gray-200/60 dark:bg-gray-900/50",
            "p-3 md:p-3",
            "rounded",
            "md:gap-4",
            "gap-2",
          ])}
          to={ROUTES.TRANSACTION_DETAIL(
            accountInfo?.account,
            transaction?.invoice,
          )}
        >
          <div className={clsx(["flex", "flex-row", "gap-2"])}>
            <div
              className={clsx([
                `!min-w-[${MOBILE_SIZE}px]`,
                `!min-h-[${MOBILE_SIZE}px]`,
                `!max-w-[${MOBILE_SIZE}px]`,
                `!max-h-[${MOBILE_SIZE}px]`,
                `md:!min-w-[${DESKTOP_SIZE}px]`,
                `md:!min-h-[${DESKTOP_SIZE}px]`,
                `md:!max-w-[${DESKTOP_SIZE}px]`,
                `md:!max-h-[${DESKTOP_SIZE}px]`,
                "rounded",
                "overflow-hidden",
                "inline-flex",
                "items-center",
                "justify-center",
                "cursor-pointer",
                "relative",
              ])}
              title={t("common:tooltips.contact_information")}
              onClick={(e: any) => {
                e?.preventDefault?.();
                setShowSourceModal(true);
              }}
              style={
                sourceInfo?.picture
                  ? {}
                  : {
                      background: rgbaObjectToString(sourceInfo?.bg),
                      color: rgbaObjectToString(sourceInfo?.color),
                    }
              }
            >
              {sourceInfo?.picture ? (
                <SmartImage
                  src={sourceInfo?.picture?.thumbnailUrl}
                  className={clsx(["bg-checkers", "!w-full", "!h-full"])}
                  alt={transaction?.payTo}
                />
              ) : (
                <Icon
                  name={sourceInfo?.icon}
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                />
              )}
            </div>
            <div className={clsx(["flex", "flex-col", "w-full"])}>
              <div
                className={clsx([
                  "text-sm",
                  "md:text-sm",
                  "font-normal",
                  "truncate",
                ])}
              >
                {description}
              </div>
              <div className={clsx(["text-sm", "truncate"])}>
                {transaction?.payTo}
              </div>
              <div className={clsx(["text-sm", "font-medium", "truncate"])}>
                {formatLongDate(transaction?.date)}
              </div>
            </div>
          </div>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-row",
              "gap-6",
              "justify-between",
              "items-center",
            ])}
          >
            <div
              className={clsx([
                "flex",
                "flex-row",
                "items-center",
                "text-xl",
                "md:text-2xl",
                "font-medium",
                "truncate",
                "dark:text-purple-400",
                "text-blue-400",
              ])}
            >
              {transaction?.type === ETransactionType.INCOME && (
                <MdArrowDownward
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                  className={clsx(["text-red-500", "dark:text-cyan-400"])}
                />
              )}
              {transaction?.type === ETransactionType.EXPENSE && (
                <MdOutlineArrowUpward
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                  className="text-red-500 dark:text-cyan-400"
                />
              )}

              <CountUp
                end={transaction?.total}
                duration={COUNT_UP_ANIMATION_DURATION}
                separator=","
                decimals={hasDecimals(transaction?.total) ? 2 : 0}
                delay={0.1}
                preserveValue
                prefix={currencySymbol}
              />
            </div>
            <ButtonGroup
              buttons={[
                {
                  tooltip: t("common:tooltips.view_transaction"),
                  icon: <MdArrowForward size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();

                    navigate(
                      ROUTES.TRANSACTION_DETAIL(
                        accountInfo?.account,
                        transaction?.invoice,
                      ),
                    );
                  },
                },
              ]}
              align="right"
            />
          </div>
        </Link>
      </motion.div>

      {showSourceModal && (
        <ClientProviderInfoModal
          clientProvider={transaction?.payTo}
          onClose={() => setShowSourceModal(false)}
        />
      )}
    </>
  );
};

export default memo(PendingTransactionCard);
